import { useState } from 'react';
import { LocationLookup } from './LocationLookup';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Condition = 'Any' | 'Fair' | 'Good' | 'Very Good' | 'Mint';

interface ValuationResult {
  priceRange: {
    min: number;
    max: number;
    currency: string;
  };
  justification: string;
  marketData: {
    recent: {
      listings: Array<{
        price: number;
        date: string;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
    current: {
      listings: Array<{
        price: number;
        location: string;
        condition: string;
        currency: string;
        url?: string;
      }>;
      explanation?: string;
    };
  };
  rawContent: string;
  citations: string[];
  searchResults: Array<{
    title: string;
    url: string;
    date: string | null;
  }>;
}

interface InstrumentProps {
  credentials: { username: string; password: string };
  dbMode: 'demo' | 'live';
  onAuthError?: () => void;
}

export function Instrument({
  credentials,
  dbMode,
  onAuthError,
}: InstrumentProps) {
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('Worldwide');
  const [condition, setCondition] = useState<Condition>('Good');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ValuationResult | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [comments, setComments] = useState('');
  const [instrumentValuationId, setInstrumentValuationId] = useState<
    string | null
  >(null);

  const [savingInstrument, setSavingInstrument] = useState(false);
  const [saveInstrumentError, setSaveInstrumentError] = useState('');

  const resetInstrumentForm = () => {
    setDescription('');
    setLocation('Worldwide');
    setCondition('Good');
    setError('');
    setResult(null);
    setRating(null);
    setComments('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    setResult(null);
    setInstrumentValuationId(null);
    try {
      const response = await fetch('/api/valuate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`,
        },
        body: JSON.stringify({ description, location, condition }),
      });

      if (response.status === 401) {
        if (onAuthError) onAuthError();
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get valuation');
      }

      const data = await response.json();
      setResult(data);

      try {
        const saveRes = await fetch('/api/instrument', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            description,
            location,
            condition,
            result: data,
            db: dbMode,
            requestedBy: credentials.username,
          }),
        });
        if (!saveRes.ok) {
          const err = await saveRes.json();
          throw new Error(err.error || 'Failed to save instrument valuation');
        }
        const saveData = await saveRes.json();
        setInstrumentValuationId(saveData.insertedId);
      } catch (saveErr) {
        setSaveInstrumentError(
          saveErr instanceof Error
            ? saveErr.message
            : 'Failed to save instrument valuation',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card className='w-full'>
        <CardHeader>
          <CardTitle>Instrument Valuation</CardTitle>
          <CardDescription>
            Enter the details of your instrument to get an estimated value
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent>
            <div className='grid w-full items-center gap-4'>
              <div className='flex flex-col space-y-1.5'>
                <Label htmlFor='description'>Instrument Description</Label>
                <Input
                  id='description'
                  placeholder="Describe your instrument (e.g., 'Fender Stratocaster 1965')"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>
              <LocationLookup
                onLocationFound={setLocation}
                defaultLocation='Worldwide'
              />
              <div className='flex flex-col space-y-1.5'>
                <Label htmlFor='condition'>Condition</Label>
                <Select
                  value={condition}
                  onValueChange={(value: Condition) => setCondition(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select condition' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='Any'>Any</SelectItem>
                    <SelectItem value='Fair'>Fair</SelectItem>
                    <SelectItem value='Good'>Good</SelectItem>
                    <SelectItem value='Very Good'>Very Good</SelectItem>
                    <SelectItem value='Mint'>Mint</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {error && <p className='text-sm text-destructive'>{error}</p>}
            </div>
          </CardContent>
          <CardFooter>
            <Button className='w-full' type='submit' disabled={isLoading}>
              {isLoading ? 'Getting Valuation...' : 'Get Valuation'}
            </Button>
          </CardFooter>
        </form>
      </Card>
      {result && (
        <Card className='mt-6 w-full'>
          <CardHeader>
            <CardTitle>Valuation Result</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='mb-6'>
              <h3 className='mb-2 text-lg font-semibold'>Price Range</h3>
              <p className='text-2xl font-bold'>
                {result.priceRange.currency}{' '}
                {result.priceRange.min.toLocaleString()} -{' '}
                {result.priceRange.max.toLocaleString()}
              </p>
            </div>
            <div className='mb-6'>
              <h3 className='mb-2 text-lg font-semibold'>Justification</h3>
              <p className='text-muted-foreground'>{result.justification}</p>
            </div>
            {result.marketData.recent?.listings?.length > 0 && (
              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Recent Sales</h3>
                <ul className='space-y-2'>
                  {result.marketData.recent.listings.map((sale, index) => (
                    <li key={index} className='border-b pb-2'>
                      <div className='flex items-start justify-between'>
                        <div>
                          <p className='font-medium'>
                            {sale.currency} {sale.price.toLocaleString()}
                          </p>
                          <p className='text-sm text-muted-foreground'>
                            {sale.date} • {sale.location}
                            {sale.condition && (
                              <span>
                                •{' '}
                                <span className='font-semibold text-primary'>
                                  {sale.condition}
                                </span>
                              </span>
                            )}
                          </p>
                        </div>
                        {sale.url && (
                          <a
                            href={sale.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-sm text-blue-500 hover:underline'
                          >
                            View Sale
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {result.marketData.recent.explanation && (
                  <p className='mt-2 text-sm text-muted-foreground'>
                    {result.marketData.recent.explanation}
                  </p>
                )}
              </div>
            )}
            {result.marketData.current?.listings?.length > 0 && (
              <div className='mb-6'>
                <h3 className='mb-2 text-lg font-semibold'>Current Listings</h3>
                <ul className='space-y-2'>
                  {result.marketData.current.listings.map((listing, index) => (
                    <li key={index} className='border-b pb-2'>
                      <div className='flex items-start justify-between'>
                        <div>
                          <p className='font-medium'>
                            {listing.currency} {listing.price.toLocaleString()}
                          </p>
                          <p className='text-sm text-muted-foreground'>
                            {listing.location}
                            {listing.condition && (
                              <span>
                                •{' '}
                                <span className='font-semibold text-primary'>
                                  {listing.condition}
                                </span>
                              </span>
                            )}
                          </p>
                        </div>
                        {listing.url && (
                          <a
                            href={listing.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-sm text-blue-500 hover:underline'
                          >
                            View Listing
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {result.marketData.current.explanation && (
                  <p className='mt-2 text-sm text-muted-foreground'>
                    {result.marketData.current.explanation}
                  </p>
                )}
              </div>
            )}
            {result.citations && result.citations.length > 0 && (
              <div className='mt-6'>
                <h3 className='mb-2 text-lg font-semibold'>Citations</h3>
                <ul className='list-disc space-y-1 pl-4'>
                  {result.citations.map((url, index) => (
                    <li key={index}>
                      <a
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-500 hover:underline'
                      >
                        {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.searchResults && result.searchResults.length > 0 && (
              <div className='mt-6'>
                <h3 className='mb-2 text-lg font-semibold'>Search Results</h3>
                <ul className='space-y-2'>
                  {result.searchResults.map((sr, index) => (
                    <li key={index} className='border-b pb-2'>
                      <a
                        href={sr.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-blue-500 hover:underline'
                      >
                        {sr.title}
                      </a>
                      {sr.date && (
                        <span className='ml-2 text-sm text-muted-foreground'>
                          ({sr.date})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {location && (
              <div className='mt-1 text-xs text-muted-foreground'>
                <span>Location: {location}</span>
              </div>
            )}
            <div className='mt-8'>
              <h4 className='mb-2 text-base font-semibold'>
                Rate this valuation
              </h4>
              <div className='mb-4 flex items-center'>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type='button'
                    onClick={() => setRating(star)}
                    className={`text-2xl focus:outline-none ${rating && star <= rating ? 'text-yellow-400' : 'text-gray-400'}`}
                    aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                  >
                    ★
                  </button>
                ))}
                {rating && (
                  <span className='ml-2 text-sm text-muted-foreground'>
                    {rating} / 5
                  </span>
                )}
              </div>
              <label
                htmlFor='valuation-comments'
                className='mb-1 block text-sm font-medium'
              >
                Comments
              </label>
              <textarea
                id='valuation-comments'
                className='w-full rounded border border-input bg-background p-2 text-sm focus:border-primary focus:outline-none'
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder='Let us know what you think about this valuation...'
              />
              <Button
                type='button'
                className='mt-4 w-full'
                disabled={savingInstrument || !instrumentValuationId}
                onClick={async () => {
                  setSaveInstrumentError('');
                  if (!instrumentValuationId) return;
                  if (!rating && !comments) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    resetInstrumentForm();
                    return;
                  }
                  setSavingInstrument(true);
                  try {
                    const response = await fetch(
                      `/api/instrument/${instrumentValuationId}`,
                      {
                        method: 'PATCH',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ rating, comments, db: dbMode }),
                      },
                    );
                    if (!response.ok) {
                      const err = await response.json();
                      throw new Error(
                        err.error || 'Failed to update instrument valuation',
                      );
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    resetInstrumentForm();
                  } catch (err) {
                    setSaveInstrumentError(
                      err instanceof Error ? err.message : 'An error occurred',
                    );
                  } finally {
                    setSavingInstrument(false);
                  }
                }}
              >
                {savingInstrument ? 'Saving...' : 'Finish'}
              </Button>
              {saveInstrumentError && (
                <p className='mt-2 text-sm text-destructive'>
                  {saveInstrumentError}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

export default Instrument;
